import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeWhile } from 'rxjs/operators';
import { LabReqBaseComponent } from '../lab-req-base/lab-req-base.component';
import { SkProfileCheckboxComponent } from './sk-profile-checkbox/sk-profile-checkbox.component';
import { SkTestSpecificationCheckboxComponent } from './sk-test-checkbox/sk-test-checkbox.component';
import { SkIndividualAnalysesCheckboxComponent } from './sk-individual-analyses-checkbox copy/sk-individual-analyses-checkbox.component';
import { DynacareLogoComponent } from './sk-health-authority-logo/sk-health-authority-logo.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SkProfileCheckboxComponent,
    SkIndividualAnalysesCheckboxComponent,
    SkTestSpecificationCheckboxComponent,
    DynacareLogoComponent
  ],
  encapsulation: ViewEncapsulation.ShadowDom,
  selector: 'app-lab-req-sk',
  templateUrl: './lab-req-sk.component.html',
  styleUrls: ['./lab-req-sk.component.less']
})
export class LabReqSkComponent extends LabReqBaseComponent implements OnInit {
  constructor(private fb: FormBuilder) {
    super();
  }

  ngOnInit() {
    this.initForm();

    this.form.get('PHN')?.valueChanges
      .pipe(takeWhile(() => this.active))
      .subscribe(phn => {
        if (this.lgvPaidSameAsPHN) {
          this.form.patchValue({ lgvPaid_healthNumber: phn || '' }, { emitEvent: false });
        }
      });
  }

  lgvPaidSameAsPHN = true;

  onHealthNumberSameAsPHN(checked: boolean) {
    this.lgvPaidSameAsPHN = checked;
    if (checked) {
      const phn = this.form.get('PHN')?.value || '';
      this.form.patchValue({ lgvPaid_healthNumber: phn }, { emitEvent: false });
    } else {
      this.form.patchValue({ lgvPaid_healthNumber: '' }, { emitEvent: false });
    }
  }

  onLgvPaidFastingNoChange(checked: boolean) {
    if (checked) {
      this.form.patchValue({ lgvPaid_fastingYes: false, lgvPaid_fastingHours: '' }, { emitEvent: false });
    }
  }

  onLgvPaidFastingYesChange(checked: boolean) {
    if (checked) {
      this.form.patchValue({ lgvPaid_fastingNo: false }, { emitEvent: false });
    }
  }

  initForm() {
    this.form = this.fb.group({
      requestingPhysicianFirstName: ['', [Validators.required]],
      requestingPhysicianLastName: ['', [Validators.required]],
      additionalCopiesOfReportTo: [''],
      collectionDate: [''],
      collectionTime: [''],
      clinicNameOrBillingInfo: [''],
      PHN: [''],
      chartNo: [''],
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      male: [false],
      female: [false],
      DOB: [''],
      phone: [''],
      otherTests: [''],
      CBC: [false],
      ARETC: [false],
      DDIM: [false],
      FIB: [false],
      PT: [false],
      APTT: [false],
      LYTE4: [false],
      CREAT: [false],
      UREA: [false],
      GLUCR: [false],
      GLUFA: [false],
      HMA1C: [false],
      CRCLE: [false],
      CRCLE_weight: [''],
      TRIG: [false],
      CHOL: [false],
      LIPNF: [false],
      LIPNF_value: [''],
      GES1H: [false],
      GES2H: [false],
      GTT2H: [false],
      ALB: [false],
      CA: [false],
      PHOS: [false],
      MG: [false],
      URIC: [false],
      ALP: [false],
      ALT: [false],
      AST: [false],
      CK: [false],
      LD: [false],
      LIP: [false],
      GGT: [false],
      BILIT: [false],
      BILFR: [false],
      BHCG: [false],
      IRONB: [false],
      FER: [false],
      PSA: [false],
      THYSA: [false],
      FRT4: [false],
      АТРА: [false],
      FSHLH: [false],
      EDIOL: [false],
      PRGE: [false],
      PRL: [false],
      WASER: [false],
      HIV: [false],
      CRPH: [false],
      RHF: [false],
      TNTHS: [false],
      TP: [false],
      PES: [false],
      FIT: [false],
      CARBZ: [false],
      DIGI: [false],
      LITH: [false],
      PHENB: [false],
      PTNY: [false],
      VALPR: [false],
      CYCL: [false],
      CY2: [false],
      TACR: [false],
      SIRO: [false],
      therapeuticDosage: [''],
      therapeuticDateTime: [''],
      PRNIM: [false],
      PRENA: [false],
      PRENP: [false],
      UA: [false],
      HCGU: [false],
      CLGP: [false],
      ALBCR: [false],
      catheterizedUrinalysis: [false],
      urineStartDate: [''],
      urineStartTime: [''],
      urineEndDate: [''],
      urineEndTime: [''],
      CAUD: [false],
      CREUD: [false],
      CRCL: [false],
      CRCLC: [false],
      CRCLC_ht: [''],
      CRCLC_wt: [''],
      PO4UD: [false],
      TPUD: [false],
      PEU: [false],
      NAKUD: [false],
      UREUD: [false],
      URAUD: [false],
      HEPPA: [false],
      HBCHR: [false],
      HCAB: [false],
      HAABT: [false],
      HBABS: [false],
      CMVA: [false],
      CMVI: [false],
      BLOOD_CULTURE_CS: [false],
      CERVIX_SWAB_GC: [false],
      SPUTUM_CS: [false],
      SPUTUM_TB_AFB: [false],
      STOOL_CS: [false],
      STOOL_OP: [false],
      STOOL_CDIFF: [false],
      THROAT_CS: [false],
      URETHRAL_SWAB_GC: [false],
      URINE_CATHETER_CS: [false],
      URINE_CATHETER_YEAST: [false],
      URINE_CATHETER_SOURCE: [''],
      URINE_MIDSTREAM_CS: [false],
      URINE_MIDSTREAM_YEAST: [false],
      VAGINAL_BV: [false],
      VAGINAL_TRICH: [false],
      VAGINAL_GROUP_B_STREP_PREGNANCY_ONLY: [false],
      OTHER_TEST: [''],
      OTHER_TEST_SOURCE: [''],
      lgvPaid_clientReference: [''],
      lgvPaid_healthNumber: [''],
      lgvPaid_danTalya: [true],
      lgvPaid_docFee: [true],
      lgvPaid_shbg: [true],
      lgvPaid_psaScreening: [false],
      lgvPaid_vitaminD25: [true],
      lgvPaid_apob: [false],
      lgvPaid_fastingNo: [true],
      lgvPaid_fastingYes: [false],
      lgvPaid_fastingHours: [''],
    });
  }
}
